import { useState, useEffect, useRef } from 'react'
import useSignupDraft from '../../hooks/useSignupDraft.jsx'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff, Upload, FileText, Sparkles } from 'lucide-react'
import publicApi from '../../services/publicApi'
import shrinkImage from '../../utils/shrinkImage'
import InstallAppButton from '../common/InstallAppButton.jsx'
import ShareTabNote from '../common/ShareTabNote.jsx'
import { formatDateTime } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Public registration page reached via the Admin-generated invite link:
 *   /consultant-invite/:token
 *
 * Flow:
 *   1. Validate token on mount (calls /onboarding/invites/{token}/validate)
 *   2. If valid, show the registration form
 *   3. Submit → POST /onboarding/invites/register → success → redirect to /login
 *
 * Uses the public API client because this page is reached BEFORE the user has a session;
 * the `api` service would attach auth headers that don't exist yet.
 */
// Mirrors the backend's is_valid_linkedin_url() exactly — a real profile URL, not just
// any linkedin.com link (e.g. a company page) or arbitrary text.
const LINKEDIN_URL_RE = /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/in\/[A-Za-z0-9\-_%]+\/?(\?.*)?$/i

// Loose token-overlap check, not exact match — "Dhiraj Mattur" vs "Dhiraj K. Mattur" or
// minor formatting differences shouldn't false-alarm. Flags only a genuinely different
// name (e.g. the CV extracted "Robert Chen" for a form that already said "Dhiraj Mattur").
function namesLikelyDiffer(typedName, cvName) {
  const norm = s => (s || '').toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/).filter(Boolean)
  const typed = norm(typedName)
  const extracted = norm(cvName)
  if (!typed.length || !extracted.length) return false
  const overlap = typed.filter(t => extracted.includes(t)).length
  return overlap === 0
}

export default function ConsultantInvitePage() {
  const { t } = useTranslation('consultants')
  const { token } = useParams()
  const navigate = useNavigate()

  const [validating, setValidating] = useState(true)
  const [invalid, setInvalid] = useState(null)
  const [expiresAt, setExpiresAt] = useState(null)
  const [role, setRole] = useState('Consultant')

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone_number: '',
    linkedin_url: '',
    specialization: '',
  organization_name: '',
  })
  const [showPwd, setShowPwd] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // ── CV upload + AI auto-fill (Phase I) ──────────────────────────────
  const cvFileRef = useRef(null)
  const [cvFileName, setCvFileName] = useState(null)   // shows ✓ after upload
  const [cvDragging, setCvDragging] = useState(false)
  const [cvParsing, setCvParsing] = useState(false)
  const [cvError, setCvError] = useState(null)
  const [cvAutofilled, setCvAutofilled] = useState(false)
  const [cvNameMismatch, setCvNameMismatch] = useState(null) // the CV's extracted name, if it looks like someone else's

  // ── KYC ID document upload + AI extraction (v2.6) ───────────────────
  const idFileRef = useRef(null)
  const [idType, setIdType] = useState('Passport')
  const [idNumber, setIdNumber] = useState('')
  const [idFileName, setIdFileName] = useState(null)
  // Read from the ID at signup and carried through to the KYC step, so the same document never has
  // to be produced twice. Not shown on this form — captured silently and pre-filled later.
  const [idNationality, setIdNationality] = useState('')
  const [idDob, setIdDob] = useState('')
  const [idAddress, setIdAddress] = useState('')
  const [idParsing, setIdParsing] = useState(false)
  const [idError, setIdError] = useState(null)

  // ── Profile picture + certifications upload (v2.6) ──────────────────
  const profileRef = useRef(null)
  const certRef = useRef(null)
  const [profileName, setProfileName] = useState(null)
  const [certName, setCertName] = useState(null)
  const [docError, setDocError] = useState(null)

  // ── SURVIVING A REFRESH ───────────────────────────────────────────
  // A consultant on a weak connection lost twenty minutes of this form to one reload. Every
  // field is written to this browser as it is typed -- never over the network, because the
  // network is the thing that is failing -- and offered back on the way in.
  //
  // The uploads are already safe: each one went to the server against the invite token as soon
  // as it was chosen. What a reload loses is the knowledge that they arrived, so the file names
  // are restored with everything else and the notice can say so.
  //
  // Passwords are not written. Everything else is keyed to this invite, so two invites opened
  // on one machine cannot pour into each other.
  const draft = useSignupDraft(token, {
    form, idType, idNumber, idFileName, idNationality, idDob, idAddress,
    cvFileName, cvAutofilled, profileName, certName,
  }, { omit: ['password', 'confirm_password'] })

  const [restoredNote, setRestoredNote] = useState(null)

  const restoreDraft = () => {
    const d = draft.restored
    if (!d) return
    if (d.form) setForm(f => ({ ...f, ...d.form, password: f.password, confirm_password: f.confirm_password }))
    if (d.idType) setIdType(d.idType)
    if (d.idNumber) setIdNumber(d.idNumber)
    if (d.idFileName) setIdFileName(d.idFileName)
    if (d.idNationality) setIdNationality(d.idNationality)
    if (d.idDob) setIdDob(d.idDob)
    if (d.idAddress) setIdAddress(d.idAddress)
    if (d.cvFileName) setCvFileName(d.cvFileName)
    if (d.cvAutofilled) setCvAutofilled(true)
    if (d.profileName) setProfileName(d.profileName)
    if (d.certName) setCertName(d.certName)
    setRestoredNote(
      (d.cvFileName || d.idFileName || d.profileName || d.certName)
        ? 'Your details are back, and the files you uploaded are still with us. Please set a password again — that is the one thing we never keep.'
        : 'Your details are back. Please set a password again — that is the one thing we never keep.'
    )
    draft.dismiss()
  }

  const uploadDoc = async (kind, file) => {
    if (!file) return
    // Shrink first — see handleIdUpload; an over-limit phone photo is usually fine once
    // re-encoded, and rejecting it before that is a dead end for no reason.
    file = await shrinkImage(file)
    if (file.size > 10 * 1024 * 1024) { setDocError('File too large (max 10 MB).'); return }
    setDocError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('token', token)
      fd.append('kind', kind)
      await publicApi.post('/onboarding/invites/upload-doc', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (kind === 'profile') setProfileName(file.name)
      else setCertName(file.name)
    } catch (e) {
      setDocError(e.response?.data?.detail || 'Could not upload this file.')
    }
  }

  const handleIdUpload = async (file) => {
    if (!file) return
    const okExt = /\.(pdf|png|jpe?g|webp)$/i.test(file.name || '')
    if (!okExt) {
      setIdError('Please upload an image (PNG/JPG) or PDF of your ID.')
      return
    }
    // A phone photo of an ID is megabytes as taken and a few hundred kilobytes re-encoded, and
    // reads just as well to the extractor. Shrink before judging the size, or a file we can
    // happily accept is refused for being what a phone camera produces.
    file = await shrinkImage(file)
    if (file.size > 10 * 1024 * 1024) {
      setIdError('File too large (max 10 MB).')
      return
    }
    setIdError(null)
    setIdParsing(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('token', token)
      const { data } = await publicApi.post('/onboarding/invites/parse-id', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (data.id_type) setIdType(data.id_type)
      if (data.id_number) setIdNumber(data.id_number)
      // Keep the nationality and date of birth the reader found. These are not asked for on this
      // form, but the KYC step needs them — capturing them here is what stops the consultant being
      // asked to upload and retype the same document a second time.
      if (data.nationality) setIdNationality(data.nationality)
      if (data.date_of_birth) setIdDob(data.date_of_birth)
      if (data.address) setIdAddress(data.address)
      if (data.full_name && !form.full_name) setForm(prev => ({ ...prev, full_name: data.full_name }))
      setIdFileName(data.id_filename || file.name)
      if (!data.id_number) {
        setIdError("We saved your ID but couldn't read the number automatically — please type it below.")
      }
    } catch (e) {
      setIdError(e.response?.data?.detail || 'Could not upload this ID. Please try a clearer photo or a PDF.')
    } finally {
      setIdParsing(false)
    }
  }

  const handleCvUpload = async (file) => {
    if (!file) return
    const ok = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    const okExt = /\.(pdf|docx)$/i.test(file.name || '')
    if (!ok.includes(file.type) && !okExt) {
      setCvError('Please upload a PDF or DOCX file.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setCvError('File too large (max 5 MB).')
      return
    }
    setCvError(null)
    setCvNameMismatch(null)
    setCvParsing(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('token', token)
      const { data } = await publicApi.post('/onboarding/invites/parse-cv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      // The backend always saves the file first, then attempts AI parsing — a scanned/
      // unreadable CV still gets attached (parse_failed: true), just without autofill.
      setCvFileName(data.cv_filename || file.name)
      if (data.parse_failed) {
        setCvError("Couldn't auto-read this CV — it's still attached; please fill the fields manually.")
      } else {
        // Compare against whatever name was ALREADY typed, before the autofill below
        // overwrites it — otherwise the comparison would trivially always match.
        if (data.full_name && form.full_name.trim() && namesLikelyDiffer(form.full_name, data.full_name)) {
          setCvNameMismatch(data.full_name)
        }
        setForm(prev => ({
          ...prev,
          full_name: data.full_name || prev.full_name,
          email: data.email || prev.email,
          phone_number: data.phone_number || prev.phone_number,
          linkedin_url: data.linkedin_url || prev.linkedin_url,
          specialization: data.specialization || prev.specialization,
        }))
        setCvAutofilled(true)
      }
    } catch (e) {
      setCvError(e.response?.data?.detail || 'Could not attach this CV. Please try a different file or fill the fields manually.')
    } finally {
      setCvParsing(false)
    }
  }

  // ── Validate invite token on mount ───────────────────────────────────────
  useEffect(() => {
    publicApi.get(`/onboarding/invites/${token}/validate`)
      .then(r => { setExpiresAt(r.data.expires_at); setRole(r.data.role || 'Consultant') })
      .catch(e => setInvalid(e.response?.data?.detail || 'Invalid invite link'))
      .finally(() => setValidating(false))
  }, [token])

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }))

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (!form.full_name.trim() || !form.email.trim() || !form.password) {
      setError('Full name, email and password are required.')
      return
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.')
      return
    }
    // Consultants must give phone + professional verification (LinkedIn or CV) for KYC.
    // Internal staff roles (HR / PM / Finance / Funder) skip all of that — name, email
    // and password are enough (the backend enforces the same: KYC gate is consultant-only).
    if (role === 'Consultant') {
      if (!form.phone_number.trim()) {
        setError('Phone number is required.')
        return
      }
      const linkedinValid = LINKEDIN_URL_RE.test(form.linkedin_url.trim())
      if (form.linkedin_url.trim() && !linkedinValid) {
        setError('Please enter a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/your-name).')
        return
      }
      if (!linkedinValid && !cvFileName) {
        setError('Please provide either your LinkedIn profile URL or upload your CV.')
        return
      }
    }
    setSubmitting(true)
    try {
      await publicApi.post('/onboarding/invites/register', {
        token,
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone_number: form.phone_number.trim() || null,
        linkedin_url: form.linkedin_url.trim() || null,
        specialization: form.specialization.trim() || null,
        organization_name: role !== 'Consultant' ? (form.organization_name.trim() || null) : null,
        id_document_type: idFileName ? idType : null,
        id_document_number: idNumber.trim() || null,
        // Carried into the KYC identity record so that step is a confirmation, not a re-entry.
        nationality: idNationality.trim() || null,
        date_of_birth: idDob.trim() || null,
        mailing_address: idAddress.trim() || null,
      })
      draft.clear()   // the account exists now; nothing left to come back to
      setSuccess(true)
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600">{t('verifying_invite')}</p>
        </div>
      </div>
    )
  }

  // ── Invalid / expired / used token ──────────────────────────────────────
  if (invalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-3">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">{t('invite_unavailable')}</h1>
          <p className="text-sm text-gray-600 mb-5">{invalid}</p>
          <p className="text-xs text-gray-400">Please contact your administrator to request a new invite link.</p>
          <Link to="/login" className="inline-block mt-4 text-sm text-blue-600 hover:underline">{t('go_to_login')}</Link>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">{t('welcome_aboard')}</h1>
          <p className="text-sm text-gray-600 mb-5">
            Your account has been created. Install the platform on your desktop for quick access, then log in with the password you just set.
          </p>
          <InstallAppButton />
          <button
            onClick={() => navigate('/login')}
            className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Continue to login
          </button>
        </div>
      </div>
    )
  }

  // ── Registration form ────────────────────────────────────────────────────
  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
  const labelCls = "block text-xs font-medium text-gray-700 mb-1"

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-extrabold text-gray-900">
            Welcome to <span className="text-transparent bg-clip-text bg-linear-to-r from-blue-600 to-indigo-600">ProTrack</span>
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            You've been invited to join as a <span className="font-semibold text-gray-800">{role}</span>. Create your account to start onboarding.
          </p>
          {expiresAt && (
            <p className="mt-2 text-xs text-amber-600">
              ⏱ This invite expires on {formatDateTime(expiresAt)}
            </p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={submit} autoComplete="off" className="bg-white rounded-2xl shadow-lg p-6 space-y-4 border border-gray-100">
          {/* Hidden honeypot inputs absorb Chrome's aggressive autofill so the real
              fields below stay blank. This is the most reliable cross-browser trick. */}
          <input type="text" name="fake-username" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, width: 0 }} />
          <input type="password" name="fake-password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, width: 0 }} />

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
          )}
          {/* Offered, not applied. Somebody who abandoned a half-filled form on purpose should
              not find it waiting for them, and somebody who lost one should not have to. */}
          {draft.restored && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <p className="text-sm text-amber-900 font-medium">You had started this form</p>
              <p className="text-xs text-amber-800 mt-0.5">
                What you typed was kept on this device
                {draft.savedAt ? ` (${new Date(draft.savedAt).toLocaleString()})` : ''}.
              </p>
              <div className="flex gap-3 mt-2">
                <button type="button" onClick={restoreDraft}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white">
                  Bring it back
                </button>
                <button type="button" onClick={draft.clear}
                  className="text-xs text-amber-800 hover:underline">
                  Start fresh
                </button>
              </div>
            </div>
          )}

          {restoredNote && (
            <div className="bg-green-50 border border-green-200 text-green-800 text-xs px-4 py-2.5 rounded-lg">
              {restoredNote}
            </div>
          )}

          {role === 'Consultant' && (<>
          {/* ─── CV Upload — Quick Fill (Phase I) ───────────────────── */}
          <div className={`rounded-xl border-2 ${cvAutofilled ? 'border-green-300 bg-green-50' : cvFileName ? 'border-amber-300 bg-amber-50' : cvDragging ? 'border-blue-400 bg-blue-50' : 'border-dashed border-blue-300 bg-blue-50/60'} p-4 transition-colors`}>
            {!cvFileName && !cvParsing && (
              <div
                onDragOver={(e) => { e.preventDefault(); setCvDragging(true) }}
                onDragLeave={() => setCvDragging(false)}
                onDrop={(e) => {
                  e.preventDefault(); setCvDragging(false)
                  const f = e.dataTransfer.files?.[0]
                  if (f) handleCvUpload(f)
                }}
                onClick={() => cvFileRef.current?.click()}
                className="cursor-pointer text-center py-4"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-linear-to-r from-blue-500 to-indigo-600 mb-2">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-semibold text-gray-800">{t('upload_cv_autofill')}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Drag &amp; drop or click. PDF or DOCX, max 5 MB. Powered by AI.
                </p>
                <input
                  ref={cvFileRef}
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleCvUpload(e.target.files[0])}
                />
              </div>
            )}
            {cvParsing && (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">{t('reading_cv')}</p>
                <p className="text-xs text-gray-500">{t('takes_seconds')}</p>
              </div>
            )}
            {cvAutofilled && !cvParsing && (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-green-800">
                    Auto-filled from <span className="font-mono">{cvFileName}</span>
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Review and edit the fields below before submitting.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setCvAutofilled(false); setCvFileName(null); setCvNameMismatch(null); cvFileRef.current && (cvFileRef.current.value = '') }}
                  className="text-xs text-blue-700 hover:underline shrink-0"
                >
                  Upload different CV
                </button>
              </div>
            )}
            {cvNameMismatch && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                ⚠ This CV appears to belong to <strong>{cvNameMismatch}</strong> — please confirm this is your CV, or that your typed name matches.
              </p>
            )}
            {cvFileName && !cvAutofilled && !cvParsing && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800">
                    CV attached: <span className="font-mono">{cvFileName}</span>
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Couldn't auto-read it — your file is still attached; fill the fields below manually.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setCvFileName(null); setCvError(null); setCvNameMismatch(null); cvFileRef.current && (cvFileRef.current.value = '') }}
                  className="text-xs text-blue-700 hover:underline shrink-0"
                >
                  Upload different CV
                </button>
              </div>
            )}
            {cvError && (
              <p className="text-xs text-red-600 mt-2">{cvError}</p>
            )}
          </div>

          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase tracking-widest">or fill manually</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          </>)}

          <div>
            <label className={labelCls}>{t('full_name_req')}</label>
            <ShareTabNote className="mb-4" />

            <input
              type="text"
              required
              value={form.full_name}
              onChange={e => set('full_name', e.target.value)}
              placeholder="e.g. Jane Smith"
              className={inputCls}
              name="invite-fullname"
              autoComplete="off"
            />
          </div>

          <div>
            <label className={labelCls}>{t('email_req')}</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="jane@example.com"
              className={inputCls}
              name="invite-email"
              autoComplete="off"
              data-form-type="other"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('password_req')}</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  required
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder={t('ph_min6')}
                  className={`${inputCls} pr-9`}
                  name="invite-new-password"
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className={labelCls}>{t('confirm_password_req')}</label>
              <input
                type={showPwd ? 'text' : 'password'}
                required
                value={form.confirm_password}
                onChange={e => set('confirm_password', e.target.value)}
                placeholder={t('ph_retype_password')}
                className={inputCls}
                name="invite-confirm-password"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Phone Number{role === 'Consultant' ? ' *' : ''}{role === 'Consultant' && <span className="ml-1 text-[10px] font-normal text-gray-400">(required)</span>}</label>
            <input
              type="tel"
              data-private
              required={role === 'Consultant'}
              value={form.phone_number}
              onChange={e => set('phone_number', e.target.value)}
              placeholder="+971 50 123 4567"
              className={inputCls}
              name="invite-phone"
              autoComplete="off"
            />
          </div>

          {role === 'Consultant' && (<>
          {/* Professional verification — LinkedIn OR uploaded CV is required */}
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
            <p className={labelCls + ' mb-2'}>
              Professional verification * <span className="text-[10px] font-normal text-gray-500">— LinkedIn URL or CV upload (above) required</span>
            </p>
            <input
              type="url"
              value={form.linkedin_url}
              onChange={e => set('linkedin_url', e.target.value)}
              placeholder="https://linkedin.com/in/your-handle"
              className={inputCls}
              name="invite-linkedin"
              autoComplete="off"
            />
            {form.linkedin_url.trim() && (
              <p className={`mt-1 text-xs flex items-center gap-1 ${LINKEDIN_URL_RE.test(form.linkedin_url.trim()) ? 'text-green-700' : 'text-amber-600'}`}>
                {LINKEDIN_URL_RE.test(form.linkedin_url.trim())
                  ? <><CheckCircle2 className="w-3.5 h-3.5" /> Looks like a valid LinkedIn profile URL</>
                  : 'Not a recognized LinkedIn profile URL yet'}
              </p>
            )}
            <div className="flex items-center justify-between mt-2 text-xs">
              <span className={cvFileName ? 'text-green-700 flex items-center gap-1' : 'text-gray-400'}>
                {cvFileName ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Verified CV: <span className="font-mono">{cvFileName}</span>
                  </>
                ) : (
                  <span className="flex items-center gap-1">
                    <FileText className="w-3.5 h-3.5" />
                    No CV uploaded yet
                  </span>
                )}
              </span>
              {!cvFileName && (
                <button
                  type="button"
                  onClick={() => cvFileRef.current?.click()}
                  className="text-blue-700 hover:underline"
                >
                  Upload one
                </button>
              )}
            </div>
          </div>
          </>)}

          {/* Project Managers, Finance and the other staff roles: two optional lines about who they are,
              the same as the sign-up form. The CV, LinkedIn and ID checks above and below are for consultants. */}
          {role !== 'Consultant' && (
            <div>
              <label className={labelCls}>Organization <span className="ml-1 text-[10px] font-normal text-gray-400">(optional)</span></label>
              <input type="text" value={form.organization_name} onChange={e => set('organization_name', e.target.value)}
                placeholder="e.g. 3 Pillars Data & Training" className={inputCls} name="invite-organization" autoComplete="organization" />
            </div>
          )}

          <div>
            <label className={labelCls}>{t('specialization')}{role !== 'Consultant' && <span className="ml-1 text-[10px] font-normal text-gray-400">(optional)</span>}</label>
            <input
              type="text"
              value={form.specialization}
              onChange={e => set('specialization', e.target.value)}
              placeholder={t('ph_specialization')}
              className={inputCls}
              name="invite-specialization"
              autoComplete="off"
            />
          </div>

          {role === 'Consultant' && (<>
          {/* ─── KYC: Government ID (auto-read with AI) ─────────────── */}
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60 space-y-2.5">
            <p className={labelCls + ' mb-0'}>
              Personal details <span className="text-[10px] font-normal text-gray-500">— upload your government ID; we read the number automatically</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <select
                value={idType}
                onChange={e => setIdType(e.target.value)}
                className={inputCls}
              >
                <option value="Passport">{t('id_passport')}</option>
                <option value="National ID">{t('id_national')}</option>
                <option value="Aadhaar">{t('id_aadhaar')}</option>
                <option value="PAN">{t('id_pan')}</option>
                <option value="Driver's License">{t('id_licence')}</option>
                <option value="Other">{t('id_other')}</option>
              </select>
              <input
                type="text"
                data-private
                value={idNumber}
                onChange={e => setIdNumber(e.target.value)}
                placeholder={t('ph_id_autofilled')}
                className={inputCls}
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => idFileRef.current?.click()}
                disabled={idParsing}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-200 bg-white rounded-md hover:bg-blue-50 disabled:opacity-50"
              >
                {idParsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {idParsing ? 'Reading ID…' : (idFileName ? 'Replace ID file' : 'Upload ID (image or PDF)')}
              </button>
              {idFileName && !idParsing && (
                <span className="text-xs text-green-700 flex items-center gap-1 min-w-0">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate font-mono">{idFileName}</span>
                </span>
              )}
              <input
                ref={idFileRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleIdUpload(e.target.files[0])}
              />
            </div>
            {idError && <p className="text-xs text-amber-600">{idError}</p>}
            <p className="text-[10px] text-gray-400">Your ID is stored securely and the number is encrypted. Visible only to you and Admin.</p>
          </div>
          </>)}

          {/* ─── Profile picture + certifications (optional) ───────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
              <p className={labelCls}>{t('profile_picture')} <span className="text-[10px] font-normal text-gray-400">(optional)</span></p>
              <button type="button" onClick={() => profileRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-200 bg-white rounded-md hover:bg-blue-50">
                <Upload className="w-3.5 h-3.5" /> {profileName ? 'Replace photo' : 'Upload photo'}
              </button>
              {profileName && <p className="text-xs text-green-700 mt-1.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /><span className="truncate">{profileName}</span></p>}
              <input ref={profileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadDoc('profile', e.target.files[0])} />
            </div>
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
              <p className={labelCls}>{t('certifications')} <span className="text-[10px] font-normal text-gray-400">(optional)</span></p>
              <button type="button" onClick={() => certRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-200 bg-white rounded-md hover:bg-blue-50">
                <Upload className="w-3.5 h-3.5" /> {certName ? 'Replace file' : 'Upload certifications'}
              </button>
              {certName && <p className="text-xs text-green-700 mt-1.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /><span className="truncate">{certName}</span></p>}
              <input ref={certRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,image/*,application/pdf" className="hidden" onChange={e => e.target.files?.[0] && uploadDoc('cert', e.target.files[0])} />
            </div>
          </div>
          {docError && <p className="text-xs text-amber-600">{docError}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-xl shadow-lg shadow-blue-500/25 disabled:opacity-50 transition-all"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</span>
            ) : (
              'Create my account'
            )}
          </button>

          <p className="text-xs text-center text-gray-400 pt-1">
            Already have an account? <Link to="/login" className="text-blue-600 hover:underline">{t('sign_in')}</Link>
          </p>
        </form>
      </div>
    </div>
  )
}
